<script setup lang="ts">
import { computed, inject, ref, toRaw } from "vue";
import {
  FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";

import { DateFormElement, FormElementType } from "~/api";
import { getCurrentDateForTimeFrames } from "~/utils/dateUtils";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";
const props = defineProps<DefaultFormComponentProps<DateFormElement>>();

const { useAnswer, substituteTemplatedText } = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const answer = useAnswer<FormElementType.DATE>(props.modelValue);

const localAnswer = ref<string | undefined>(
  structuredClone(toRaw(answer.value)),
);

const handleUpdateAnswer = (newVal: Date) => {
  answer.value = formatDate(
    newVal,
    props.modelValue.properties.enableTime ?? false,
  );
};

const formatDate = (date: Date, enableTime: boolean): string => {
  const isoString = date?.toISOString();
  return enableTime ? isoString : isoString.split("T")[0];
};

const startDate = computed(() => {
  const { startDateCode } = props.modelValue.properties;
  return startDateCode ? computeDateFromCode(startDateCode) : undefined;
});
const minDate = computed(() => {
  const { minDateCode } = props.modelValue.properties;
  return minDateCode ? computeDateFromCode(minDateCode) : undefined;
});
const maxDate = computed(() => {
  const { maxDateCode } = props.modelValue.properties;
  return maxDateCode ? computeDateFromCode(maxDateCode) : undefined;
});

const computeDateFromCode = (dateCode: string) => {
  /*
raw date:
- 2010-12-31
offset:
- today
- d+x, d-x
  - d=today, m=month, y=year
- :s, :e -- start or end of the target range

Sample date codes:
2021-12-31
today
d-1
d-14
m-1:s
m-5:e
y-100:s
y+1:e
*/
  let finalDate = getCurrentDateForTimeFrames();
  finalDate.setHours(0, 0, 0, 0); // remove time

  if (dateCode === "today") {
    // // return string, padding month and day with 0 if needed
    // const month = (finalDate.getMonth() + 1).toString().padStart(2, "0");
    // const day = finalDate.getDate().toString().padStart(2, "0");
    // return `${finalDate.getFullYear()}-${month}-${day}`;
    return finalDate;
  }

  // parse the string... if a valid date, return it
  const dateParts = dateCode.split("-");
  if (dateParts.length === 3) {
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1;
    const day = parseInt(dateParts[2]);
    finalDate = new Date(year, month, day);
    return finalDate;
  }

  // decode the code
  const parts = dateCode.split(/:/);
  const mainCode = parts[0];
  const startEnd = parts[1] ?? "";
  const range = mainCode[0].toLowerCase();
  const delta = mainCode[1];
  const offset = parseInt(mainCode.slice(2));

  switch (range) {
    case "d":
      finalDate.setDate(
        finalDate.getDate() + (delta === "+" ? offset : -offset),
      );
      break;
    case "m":
      finalDate.setMonth(
        finalDate.getMonth() + (delta === "+" ? offset : -offset),
      );
      break;
    case "y":
      finalDate.setFullYear(
        finalDate.getFullYear() + (delta === "+" ? offset : -offset),
      );
      break;
  }

  if (startEnd) {
    if (startEnd === "e") {
      switch (range) {
        case "d":
          finalDate.setHours(23, 59, 59, 999);
          break;
        case "m":
          finalDate.setMonth(finalDate.getMonth() + 1);
          finalDate.setDate(0);
          finalDate.setHours(23, 59, 59, 999);
          break;
        case "y":
          finalDate.setFullYear(finalDate.getFullYear() + 1);
          finalDate.setMonth(0);
          finalDate.setDate(0);
          finalDate.setHours(23, 59, 59, 999);
          break;
      }
    } else {
      switch (range) {
        case "d":
          finalDate.setHours(0, 0, 0, 0);
          break;
        case "m":
          finalDate.setDate(1);
          finalDate.setHours(0, 0, 0, 0);
          break;
        case "y":
          finalDate.setMonth(0);
          finalDate.setDate(1);
          finalDate.setHours(0, 0, 0, 0);
          break;
      }
    }
  }

  return finalDate;
};

const dateIsDisabled = (date: Date) => {
  let isDisabled = false;

  // Inclusive min/max
  if (minDate.value) {
    isDisabled = isDisabled || date < minDate.value;
  }

  if (maxDate.value) {
    isDisabled = isDisabled || date > maxDate.value;
  }

  return isDisabled;
};
</script>

<template id="form-date-component">
  <div class="DateComponent">
    <slot name="label" :element="modelValue" />

    <div style="flex-grow: 1"></div>
    <el-date-picker
      v-model="localAnswer"
      :type="modelValue.properties.enableTime ? 'datetime' : 'date'"
      :format="
        modelValue.properties.enableTime ? 'YYYY/MM/DD HH:mm:ss' : 'YYYY MMMM D'
      "
      :default-value="startDate"
      :disabled-date="dateIsDisabled"
      :disabled="isReadOnly"
      :placeholder="
        substituteTemplatedText(modelValue.properties.placeholder) ?? ''
      "
      @change="handleUpdateAnswer"
    />
  </div>
</template>

<style lang="less">
.DateComponent {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;

  .date-label {
    font-size: 1em;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .date-optional {
    font-size: 1em;
    font-style: italic;
    color: #999;
    display: inline;
  }

  .el-input__wrapper {
    flex-direction: row-reverse;
    justify-content: flex-end;
    .el-input__prefix {
      padding: 0 6px 0 0;
    }
    .el-input__inner {
      padding: 0 3px 0 0;
    }
  }

  .el-input__prefix-inner > :first-child,
  .el-input__prefix-inner > :first-child.el-input__icon {
    margin: 0 0 0 5px;
  }
  .el-input.is-disabled {
    .el-input__wrapper {
      background-color: #f2f2f2;
    }
    .el-input__inner {
      -webkit-text-fill-color: #444142;
    }
  }
}

body.forPdf .DateComponent {
  .el-input__wrapper {
    width: fit-content;
  }
}
</style>
